import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import { ModalCloseButton } from './modal-close-button';
import { ModalContent } from './modal-content';
import { ModalContextProvider } from './modal-context';
import { ModalDescription } from './modal-description';
import { ModalEscKeyHandler } from './modal-esc-key-handler';
import { ModalFixedBottomPanel } from './modal-fixed-bottom-panel';
import { ModalFixedTopPanel } from './modal-fixed-top-panel';
import { ModalOverlay } from './modal-overlay';
import { ModalTitle } from './modal-title';
import { ModalWrapper } from './modal-wrapper';
import { ModalProps } from './types';

type Props = ModalProps & {
    children?: ReactNode;
    closeOnEscKey?: boolean;
    closeOnOverlayClick?: boolean;
    /** When false, body scroll stays enabled (e.g. toast-style modals). Default true. */
    lockScroll?: boolean;
    focusScope?: boolean;
    overlayProps?: WithDataAttributes<ComponentPropsWithoutRef<typeof ModalOverlay>>;
    wrapperProps?: WithDataAttributes<ComponentPropsWithoutRef<typeof ModalWrapper>>;
};
const Root = ({
    children,
    overlayProps,
    wrapperProps,
    closeOnOverlayClick = true,
    closeOnEscKey = true,
    lockScroll = true,
    focusScope = true,
    ...props
}: Props) => {
    return (
        <ModalContextProvider {...props}>
            <ModalEscKeyHandler enabled={closeOnEscKey} modalId={props.modalId} />
            <ModalOverlay
                lockScroll={lockScroll}
                onClick={!closeOnOverlayClick ? () => {} : undefined}
                {...overlayProps}
            >
                <ModalWrapper focusScope={focusScope} {...wrapperProps}>
                    {children}
                </ModalWrapper>
            </ModalOverlay>
        </ModalContextProvider>
    );
};

export const Modal: typeof Root & {
    Title: typeof ModalTitle;
    Description: typeof ModalDescription;
    CloseButton: typeof ModalCloseButton;
    Content: typeof ModalContent;
    FixedTopPanel: typeof ModalFixedTopPanel;
    FixedBottomPanel: typeof ModalFixedBottomPanel;
} = Object.assign(Root, {
    Title: ModalTitle,
    Description: ModalDescription,
    CloseButton: ModalCloseButton,
    Content: ModalContent,
    FixedTopPanel: ModalFixedTopPanel,
    FixedBottomPanel: ModalFixedBottomPanel,
});
